import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DOCUMENT, documentSubSectionTitleSx } from '../constants/documentTheme';
import {
  buildPartnerOpsHomeModel,
  clientNameFromTrip,
  tripDestinationLabel,
  tripTimingLabelKey,
} from '../utils/partnerOpsHome';

const metricTileSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  p: 1.75,
  minHeight: 76,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'inherit',
  color: 'inherit',
  '&:hover': { bgcolor: '#F5F0E6' },
};

const actionBtnSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  justifyContent: 'flex-start',
  textAlign: 'left',
  borderRadius: 0,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  bgcolor: DOCUMENT.surface,
  color: DOCUMENT.ink,
  px: 2,
  py: 1.75,
  fontFamily: DOCUMENT.sans,
  fontSize: 13,
  letterSpacing: '0.04em',
  textTransform: 'none',
  '&:hover': { bgcolor: '#F5F0E6', borderColor: DOCUMENT.ruleHard },
};

const PartnerMobileHome = ({
  trips = [],
  bookings = [],
  inquiries = [],
  flightAlerts = 0,
  now,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeListRef = useRef(null);

  const model = useMemo(
    () => buildPartnerOpsHomeModel({ trips, bookings, inquiries, flightAlerts, now }),
    [trips, bookings, inquiries, flightAlerts, now]
  );

  useEffect(() => {
    // Keep metrics fresh when parent re-fetches; no local timer needed.
  }, [model.clientsTraveling, model.approvalsWaiting, model.clientMessages]);

  const scrollToActive = () => {
    activeListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const metrics = [
    {
      id: 'traveling',
      label: t('partner.opsHome.metricTraveling'),
      value: model.clientsTraveling,
      onClick: () => navigate('/partner/trips?ops=traveling'),
    },
    {
      id: 'approvals',
      label: t('partner.opsHome.metricApprovals'),
      value: model.approvalsWaiting,
      onClick: () => navigate('/partner/bookings'),
    },
    {
      id: 'messages',
      label: t('partner.opsHome.metricMessages'),
      value: model.clientMessages,
      onClick: () => navigate('/partner/inquiries'),
    },
    {
      id: 'departures',
      label: t('partner.opsHome.metricDepartures'),
      value: model.upcomingDepartures,
      onClick: () => navigate('/partner/trips?ops=departing'),
    },
    {
      id: 'flights',
      label: t('partner.opsHome.metricFlights'),
      value: model.flightAlerts,
      onClick: () => navigate('/partner/trips?ops=flights'),
    },
  ];

  const primaryActions = [
    {
      id: 'createTrip',
      label: t('partner.opsHome.actionCreateTrip'),
      onClick: () => navigate('/trips/create'),
    },
    {
      id: 'customizePackage',
      label: t('partner.opsHome.actionCustomizePackage'),
      onClick: () => navigate('/partner/packages?customize=1'),
    },
    {
      id: 'manageTrip',
      label: t('partner.opsHome.actionManageTrip'),
      onClick: () => {
        if (model.activeTrips[0]?.id) {
          navigate(`/partner/trips/${model.activeTrips[0].id}`);
          return;
        }
        scrollToActive();
      },
    },
    {
      id: 'respondRequest',
      label: t('partner.opsHome.actionRespondRequest'),
      onClick: () => navigate('/partner/inquiries'),
    },
  ];

  return (
    <Box data-testid="partner-mobile-home" sx={{ pt: 2.5, width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Typography
        component="h1"
        sx={{ fontFamily: DOCUMENT.display, fontSize: 28, fontWeight: 400, lineHeight: 1.15 }}
      >
        {t('partner.opsHome.title')}
      </Typography>
      <Typography sx={{ mt: 0.75, fontSize: 13.5, color: DOCUMENT.ink2, lineHeight: 1.5 }}>
        {t('partner.opsHome.subtitle')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 1.25,
          mt: 2.5,
          width: '100%',
          minWidth: 0,
        }}
      >
        {metrics.map((metric) => (
          <Box
            key={metric.id}
            component="button"
            type="button"
            data-testid={`partner-ops-metric-${metric.id}`}
            onClick={metric.onClick}
            sx={{ ...metricTileSx, minWidth: 0, maxWidth: '100%' }}
          >
            <Typography sx={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
              {metric.label}
            </Typography>
            <Typography sx={{ fontSize: 26, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
              {metric.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography
        component="h2"
        sx={{ ...documentSubSectionTitleSx, mt: 3.5, mb: 1.25, fontSize: 20 }}
      >
        {t('partner.opsHome.primaryActions')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {primaryActions.map((action) => (
          <Button
            key={action.id}
            data-testid={`partner-ops-action-${action.id}`}
            onClick={action.onClick}
            sx={actionBtnSx}
          >
            {action.label}
          </Button>
        ))}
      </Box>

      <Box ref={activeListRef} sx={{ mt: 3.5 }}>
        <Typography
          component="h2"
          sx={{ ...documentSubSectionTitleSx, mb: 1.25, fontSize: 20 }}
        >
          {t('partner.opsHome.activeTrips')}
        </Typography>
        {model.activeTrips.length === 0 ? (
          <Typography sx={{ py: 2, fontSize: 13.5, color: DOCUMENT.ink3 }}>
            {t('partner.opsHome.noActiveTrips')}
          </Typography>
        ) : (
          model.activeTrips.map((trip) => {
            const timing = tripTimingLabelKey(trip, model.today);
            const dest = tripDestinationLabel(trip);
            return (
              <Box
                key={trip.id}
                component="button"
                type="button"
                data-testid={`partner-ops-active-trip-${trip.id}`}
                onClick={() => navigate(`/partner/trips/${trip.id}`)}
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  border: 0,
                  borderBottom: `1px solid ${DOCUMENT.rule}`,
                  bgcolor: 'transparent',
                  py: 2,
                  px: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  color: 'inherit',
                }}
              >
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, lineHeight: 1.2 }}>
                  {clientNameFromTrip(trip)}
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: 13.5, color: DOCUMENT.ink2 }}>
                  {dest || trip.title || trip.name}
                  {' · '}
                  {t(`partner.opsHome.${timing.key}`, timing.values)}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default PartnerMobileHome;
